import { isEmpty, merge } from 'lodash-es'
import { computed, inject, ref, type ComputedRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import dayjs from 'dayjs'

import { CALL_STORAGE_KEY, INITIAL_CALL_STATE, type Call } from '@lib/types/call'
import type { User } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useCanvas from '@canvas-builder/composables/useCanvas'

export interface DefaultCrmShape {
  account?: {
    name?: string
  }
  meeting?: {
    subject?: string
    description?: string
  }
  today?: {
    date: string
    time: string
    date_time: string
  }
  user?: {
    name: string
    first_name: string
    last_name: string
  }
}

const realTimeExtraCrmShape = ref<Record<string, any>>({})

/**
 * Recursively builds a schema from an object, replacing leaf values with null
 * For arrays, uses the first element as the schema template
 */
function buildSchemaFromData(data: unknown): unknown {
  if (data === null || data === undefined) {
    return null
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return []
    }
    // Use first element as template for array schema
    return [buildSchemaFromData(data[0])]
  }

  if (typeof data === 'object') {
    const schema: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      schema[key] = buildSchemaFromData(value)
    }
    return schema
  }

  // Leaf value - return null as placeholder
  return null
}

function isEmptyValue(value: unknown): boolean {
  return !value || (typeof value === 'string' && value.trim() === '')
}

function removeEmptyValues(obj: DefaultCrmShape): Partial<DefaultCrmShape> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (isEmptyValue(value)) continue

    if (typeof value === 'object' && value !== null) {
      const nested = removeEmptyValues(value as Record<string, unknown>)
      if (Object.keys(nested).length > 0) {
        result[key] = nested
      }
      continue
    }

    result[key] = value
  }

  return result as Partial<DefaultCrmShape>
}

export default function useCrmShape() {
  const { activeCanvas } = useCanvas()

  const call = useLocalStorage<Call>(CALL_STORAGE_KEY, INITIAL_CALL_STATE, {
    deep: true,
    listenToStorageChanges: true,
  })

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const extraCrmShape = inject<ComputedRef<Record<string, any>>>(
    'extraCrmShape',
    computed(() => ({})),
  )

  const myUser = inject<ComputedRef<User | null>>(
    'myUser',
    computed(() => null),
  )

  // Derive schema from pitcher_data in canvas context if it exists
  const pitcherDataSchema = computed(() => {
    const pitcherData = activeCanvas.value?.context?.pitcher_data
    if (!pitcherData || typeof pitcherData !== 'object') {
      return {}
    }
    return { pitcher_data: buildSchemaFromData(pitcherData) }
  })

  function getDefaultCrmShape({
    accountName = '',
    myUser = null,
    showDefault = true,
  }: {
    accountName?: string
    myUser: User | null
    showDefault?: boolean
  }) {
    if (!showDefault) return {}

    return removeEmptyValues({
      account: {
        name: call.value?.selectedAccount?.Name ?? accountName,
      },
      meeting: {
        subject: call.value?.selectedEvent?.Subject ?? '',
        description: call.value?.selectedEvent?.Description ?? '',
      },
      today: {
        date: dayjs().format('MM/DD/YYYY'),
        time: dayjs().format('HH:mm'),
        date_time: `${dayjs().format('MM/DD/YYYY')}, ${dayjs().format('HH:mm')}`,
      },
      user: {
        name: myUser?.name ?? '',
        first_name: myUser?.first_name ?? '',
        last_name: myUser?.last_name ?? '',
      },
    })
  }

  function setRealTimeExtraCrmShape(shape: Record<string, any>) {
    realTimeExtraCrmShape.value = shape
  }

  const crmShape = computed(() => {
    return merge(
      {},
      launchDarkly.value.context_crm_shape ?? {},
      getDefaultCrmShape({
        myUser: myUser.value,
        showDefault: !!launchDarkly.value.enable_default_crm_shape,
      }),
      isEmpty(realTimeExtraCrmShape.value) ? {} : realTimeExtraCrmShape.value,
      isEmpty(extraCrmShape.value) ? {} : extraCrmShape.value,
      // Derive schema from actual pitcher_data in canvas context
      isEmpty(pitcherDataSchema.value) ? {} : pitcherDataSchema.value,
    )
  })

  return {
    crmShape,
    getDefaultCrmShape,
    setRealTimeExtraCrmShape,
  }
}
